import React, { useEffect, useMemo, useRef, useState } from "react";
import { Plus, Loader2, MessageCircle, Send, RefreshCw, Lock, User } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { fmtDateTime } from "../../lib/ist";

const STATUS_COLORS = {
  open: "bg-amber-500/20 text-amber-300 border-amber-500/40",
  pending_customer: "bg-blue-500/20 text-blue-300 border-blue-500/40",
  resolved: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  closed: "bg-slate-500/20 text-slate-300 border-slate-500/40",
};
const STATUS_LABELS = {
  open: "Open", pending_customer: "Awaiting your reply",
  resolved: "Resolved", closed: "Closed",
};

export default function Tickets() {
  const [items, setItems] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [form, setForm] = useState({ subject: "", message: "", priority: "normal" });
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const messagesEnd = useRef(null);

  const load = async (preserveActive = true) => {
    try {
      const { data } = await api.get("/tickets/me");
      setItems(data);
      if (preserveActive && active) {
        const updated = data.find(t => t.id === active.id);
        if (updated) setActive(updated);
      }
    } catch { /* silent */ }
  };
  useEffect(() => { load(false); }, []);

  // Live poll when viewing a thread
  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => load(true), 8000);
    return () => clearInterval(t);
  }, [active?.id]);

  useEffect(() => {
    if (messagesEnd.current) messagesEnd.current.scrollIntoView({ behavior: "smooth" });
  }, [active?.messages?.length]);

  const create = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/tickets", form);
      toast.success("Ticket created");
      setOpenModal(false);
      setForm({ subject: "", message: "", priority: "normal" });
      await load(false);
      setActive(data);
    } catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  const send = async () => {
    if (!reply.trim() || !active) return;
    setSending(true);
    try {
      await api.post(`/tickets/${active.id}/reply`, { message: reply });
      setReply("");
      await load(true);
    } catch { toast.error("Reply failed"); }
    finally { setSending(false); }
  };

  const markResolved = async () => {
    if (!active) return;
    try {
      await api.post(`/tickets/${active.id}/status`, { status: "resolved" });
      toast.success("Marked resolved");
      await load(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-5" data-testid="customer-tickets-page">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold text-white" style={{ fontFamily: "Outfit" }}>Support</h1>
          <p className="text-slate-400 text-sm">Your tickets sync in real-time with our team.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => load(true)} className="btn-ghost !py-2 !px-3"><RefreshCw size={14} /></button>
          <button onClick={() => setOpenModal(true)} className="btn-primary inline-flex items-center gap-2" data-testid="new-ticket-btn"><Plus size={16} /> New Ticket</button>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-4">
        <div className="lg:col-span-4 space-y-2 max-h-[68vh] overflow-y-auto pr-1">
          {items.length === 0 ? (
            <div className="card p-5 text-slate-500 text-sm">No tickets yet.</div>
          ) : items.map(t => (
            <button key={t.id} onClick={() => setActive(t)}
              className={`card p-4 w-full text-left border ${active?.id === t.id ? "border-blue-500/60" : "border-transparent hover:border-slate-700"}`}
              data-testid={`ticket-row-${t.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="text-white font-medium truncate flex-1">{t.subject}</div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full border ${STATUS_COLORS[t.status || "open"]}`}>{STATUS_LABELS[t.status || "open"]}</span>
              </div>
              <div className="text-slate-500 text-[11px] mt-1">{fmtDateTime(t.updated_at || t.created_at)} · {(t.messages || []).length} msg</div>
            </button>
          ))}
        </div>

        <div className="lg:col-span-8">
          {!active ? (
            <div className="card p-12 text-center text-slate-500 flex flex-col items-center gap-3">
              <MessageCircle size={36} className="opacity-40" />
              <div>Select a ticket on the left, or create a new one.</div>
            </div>
          ) : (
            <div className="card flex flex-col h-[68vh]">
              <div className="p-4 border-b border-slate-800 flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <h3 className="text-white font-semibold truncate">{active.subject}</h3>
                  <div className="text-xs text-slate-400 mt-1">Priority: <span className="text-slate-200 capitalize">{active.priority}</span> · {fmtDateTime(active.created_at)}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2.5 py-1.5 rounded-lg border ${STATUS_COLORS[active.status || "open"]}`}>{STATUS_LABELS[active.status || "open"]}</span>
                  {active.status !== "resolved" && active.status !== "closed" && (
                    <button onClick={markResolved} className="btn-ghost !py-1.5 !px-3 text-xs" data-testid="mark-resolved">Mark resolved</button>
                  )}
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {(active.messages || []).map((m, i) => (
                  <div key={i} className={`flex ${m.by === "customer" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${m.by === "customer" ? "bg-cyan-500 text-slate-900" : "bg-slate-800 text-slate-200"}`}>
                      <div className={`text-[10px] uppercase tracking-wider mb-1 ${m.by === "customer" ? "text-slate-700" : "text-slate-500"}`}>
                        {m.by === "customer" ? "You" : "Optivize Support"} · {fmtDateTime(m.at)}
                      </div>
                      {m.text}
                    </div>
                  </div>
                ))}
                <div ref={messagesEnd} />
              </div>
              {active.status === "closed" ? (
                <div className="p-4 border-t border-slate-800 text-center text-slate-500 text-sm flex items-center justify-center gap-2">
                  <Lock size={14} /> Ticket closed by support. Create a new ticket if you need further help.
                </div>
              ) : (
                <div className="p-3 border-t border-slate-800 flex gap-2">
                  <textarea
                    value={reply}
                    onChange={e => setReply(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(); }}
                    rows="2" className="input flex-1 resize-none"
                    placeholder="Reply… (⌘/Ctrl + Enter to send)"
                    data-testid="customer-reply-input"
                  />
                  <button onClick={send} disabled={sending || !reply.trim()} className="btn-primary px-4 inline-flex items-center gap-2" data-testid="customer-reply-send">
                    {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {openModal && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-5" onClick={() => setOpenModal(false)}>
          <form onClick={e => e.stopPropagation()} onSubmit={create} className="card p-6 w-full max-w-md space-y-3">
            <h3 className="text-xl font-bold text-white" style={{ fontFamily: "Outfit" }}>New Ticket</h3>
            <div><label className="label">Subject</label><input required value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} className="input w-full" data-testid="ticket-subject" /></div>
            <div><label className="label">Priority</label>
              <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })} className="input w-full">
                {["low", "normal", "high", "urgent"].map(p => <option key={p}>{p}</option>)}
              </select>
            </div>
            <div><label className="label">Message</label><textarea required value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} className="input w-full" rows={4} data-testid="ticket-message" /></div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setOpenModal(false)} className="btn-ghost">Cancel</button>
              <button type="submit" disabled={busy} className="btn-primary">{busy ? <Loader2 size={14} className="animate-spin" /> : "Submit"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
